/**
 * Versioned HTTP contract — economy-chain `v1` — for the Economy Chain backend
 * (mounted same-origin at `/api/economy-chain/v1/*` on the economy-radar Pages
 * project; ECONOMY-1 phase C1).
 *
 * Same rules as `./v1.js`: both sides parse the SAME Zod shapes (ADR 0005); the
 * `{data, error, meta}` envelope and kernel-mirrored error kinds are reused
 * verbatim; breaking changes ship as `economy-chain-v2` alongside, never by
 * mutating these.
 *
 * Two seams:
 *
 * 1. **Chain workspace** — one GET to load the researched dataset + the analyst
 *    overlay, one POST per domain mutation (`ChainOp`, mirroring the web app's
 *    optimistic `ChainGateway.apply` 1:1). **The workspace is NOT on the wire**
 *    (ECONOMY-11 / ADR 0105): the server derives it from the verified Cloudflare
 *    Access identity and the committed policy. A client cannot name, guess or
 *    change the workspace it reads and writes.
 *
 * 2. **Sweeps** — the background-worker REST contract documented by the design
 *    reference's `sweep-api.js`: start / list / cancel / retry, with clients
 *    POLLING `GET /sweeps` for progress (no websockets — ADR 0051).
 */
import { z } from 'zod';
import {
  ChainIntakeDocumentSchema,
  ChainIntakeRoundSchema,
  ChainOpRecordSchema,
  ChainOpSchema,
  ChainOverlaySchema,
  ChainProductSchema,
  ChainStagedProductSchema,
  ColumnMappingSchema,
  IntakeCaptureSchema,
  StagedProductOverridesSchema,
  SweepAreaSchema,
  SweepKindSchema,
  SweepSchema,
  SweepScopeSchema,
} from '../economy-chain/domain.js';

export const ECONOMY_CHAIN_HTTP_CONTRACT_VERSION = 'economy-chain-v1' as const;

// ── Chain workspace ──────────────────────────────────────────────────────────

/**
 * GET /api/economy-chain/v1/chain → the full working state: the researched
 * dataset (seeded until live ingestion lands) plus this workspace's analyst overlay
 * (empty overlay for a fresh workspace — never null, so clients need no seed branch).
 */
export const ChainGetResponseSchema = z.object({
  products: z.array(ChainProductSchema),
  intakes: z.array(ChainIntakeRoundSchema),
  overlay: ChainOverlaySchema,
  /**
   * The owner's uploads and where each one has got to (ADR 0100 §4).
   *
   * Carried here rather than left to `GET /intake/documents` so the intake view can render
   * "N rows waiting for review" from the SAME load that brings the rounds — one request, one
   * reload path, and no window where the rounds say one thing and the documents another.
   * Defaulted so an older server (or the mock) parses without it.
   */
  documents: z.array(ChainIntakeDocumentSchema).default([]),
});
export type ChainGetResponse = z.infer<typeof ChainGetResponseSchema>;

/**
 * POST /api/economy-chain/v1/ops — persist ONE domain mutation. The client applies
 * the op optimistically and rolls back on error; the server applies it to the
 * overlay atomically. `demo.reset` clears the owner's overlay.
 */
export const ChainOpPostRequestSchema = z.object({
  op: ChainOpSchema,
  /** Client ULID idempotency key — a retried post lands in the ledger once (ADR 0095).
   * The server generates one when absent (losing retry dedup for that post). */
  opId: z.string().min(1).optional(),
  /** The UI command id that caused this op — threads the ledger's audit trail (ADR 0095). */
  correlationId: z.string().min(1).optional(),
});
export type ChainOpPostRequest = z.infer<typeof ChainOpPostRequestSchema>;

/** Response to an op post — the applied kind + its ledger position (ADR 0095). */
export const ChainOpPostResponseSchema = z.object({
  applied: z.literal(true),
  kind: z.string().min(1),
  /** The op's `chain_ops.seq` — its place in the owner's history. */
  seq: z.number().int().positive(),
});
export type ChainOpPostResponse = z.infer<typeof ChainOpPostResponseSchema>;

// ── History (the op ledger read model, ADR 0095) ─────────────────────────────

/**
 * GET /api/economy-chain/v1/history[?productId|&category][&limit][&before]
 * — the audit trail, newest first. `before` pages by seq.
 */
export const ChainHistoryResponseSchema = z.object({
  ops: z.array(ChainOpRecordSchema),
});
export type ChainHistoryResponse = z.infer<typeof ChainHistoryResponseSchema>;

// ── Intake documents (ADR 0098) ──────────────────────────────────────────────

/**
 * `POST /api/economy-chain/v1/intake/documents` — upload one research document.
 *
 * The ONE route in this contract that is not JSON: the body is `multipart/form-data`
 * with `roundId`, `objective` and the `file` part, because a document is
 * bytes and base64-in-JSON would inflate a 20 MB report by a third for no gain. The
 * response is the standard envelope like everything else.
 *
 * It returns as soon as the bytes are stored and the extraction is queued — extraction
 * itself runs on the sweep queue, so the caller polls `GET /sweeps` for progress and
 * never holds a request open across a model call.
 */
export const IntakeDocumentUploadResponseSchema = z.object({
  document: ChainIntakeDocumentSchema,
  /** The `document` sweep now extracting it — poll `GET /sweeps` for its progress. */
  sweepId: z.string().min(1),
  /** The round the document landed on, as the server now holds it. */
  round: ChainIntakeRoundSchema,
});
export type IntakeDocumentUploadResponse = z.infer<typeof IntakeDocumentUploadResponseSchema>;

/** `GET /api/economy-chain/v1/intake/documents` → every upload, newest first. */
export const IntakeDocumentListResponseSchema = z.object({
  documents: z.array(ChainIntakeDocumentSchema),
});
export type IntakeDocumentListResponse = z.infer<typeof IntakeDocumentListResponseSchema>;

// ── Staged review: nothing is committed without a person (ADR 0100 §4) ───────

/**
 * `GET /api/economy-chain/v1/intake/staged[?documentId=…]` — the review queue.
 *
 * Returns the mapped rows AND the capture they came from, because the review screen shows
 * both side by side: the analyst checks our reading against their own cells without having
 * to reopen the file. `mapping.reading` is the model's one-line account of how it read the
 * table, which is the fastest way to spot a whole column mapped to the wrong field.
 */
export const IntakeStagedResponseSchema = z.object({
  rows: z.array(ChainStagedProductSchema),
  /** Present when a single document was asked for — the L2 capture and the mapping used. */
  capture: IntakeCaptureSchema.nullable().default(null),
  mapping: ColumnMappingSchema.nullable().default(null),
});
export type IntakeStagedResponse = z.infer<typeof IntakeStagedResponseSchema>;

/**
 * `PATCH /api/economy-chain/v1/intake/staged/:id` — one analyst edit (L4).
 *
 * Field-at-a-time and MERGED server-side, so an edit cannot clobber corrections the analyst
 * already made to other fields of the same row.
 */
export const IntakeStagedPatchRequestSchema = z.object({
  overrides: StagedProductOverridesSchema.optional(),
  /** Untick a row to keep it out of the commit without deleting it. */
  included: z.boolean().optional(),
});
export type IntakeStagedPatchRequest = z.infer<typeof IntakeStagedPatchRequestSchema>;

export const IntakeStagedPatchResponseSchema = z.object({ row: ChainStagedProductSchema });
export type IntakeStagedPatchResponse = z.infer<typeof IntakeStagedPatchResponseSchema>;

/**
 * `POST /api/economy-chain/v1/intake/documents/:id/submit` — commit the reviewed rows.
 *
 * THE only path from staging into the workspace. Commits `mapped` with `overrides` laid over
 * it, one ledger op per row with a deterministic id, so a Submit that half-succeeds can simply
 * be pressed again.
 */
export const IntakeSubmitRequestSchema = z.object({});
export type IntakeSubmitRequest = z.infer<typeof IntakeSubmitRequestSchema>;

export const IntakeSubmitResponseSchema = z.object({
  committed: z.number().int().nonnegative(),
  /** Rows whose product name already existed in the workspace. */
  duplicates: z.number().int().nonnegative(),
  /** Rows the analyst unticked. */
  excluded: z.number().int().nonnegative(),
  products: z.array(ChainProductSchema),
});
export type IntakeSubmitResponse = z.infer<typeof IntakeSubmitResponseSchema>;

/** `POST /api/economy-chain/v1/intake/documents/:id/discard` — throw the whole batch away. */
export const IntakeDiscardRequestSchema = z.object({});
export type IntakeDiscardRequest = z.infer<typeof IntakeDiscardRequestSchema>;

export const IntakeDiscardResponseSchema = z.object({ discarded: z.number().int().nonnegative() });
export type IntakeDiscardResponse = z.infer<typeof IntakeDiscardResponseSchema>;

// ── Sweeps (background AI workers) ───────────────────────────────────────────

/** POST /api/economy-chain/v1/sweeps — start a background sweep. → Sweep */
export const SweepStartRequestSchema = z.object({
  kind: SweepKindSchema,
  area: SweepAreaSchema,
  label: z.string().min(1),
  scope: SweepScopeSchema,
  /** Channels to comb; the server defaults per kind when omitted. */
  sources: z.array(z.string()).optional(),
});
export type SweepStartRequest = z.infer<typeof SweepStartRequestSchema>;

/** GET /api/economy-chain/v1/sweeps → every sweep, newest first (clients poll). */
export const SweepListResponseSchema = z.object({
  sweeps: z.array(SweepSchema),
});
export type SweepListResponse = z.infer<typeof SweepListResponseSchema>;

/** POST /api/economy-chain/v1/sweeps/:id/cancel — fail a queued/running sweep ("cancelled by analyst"). */
export const SweepCancelRequestSchema = z.object({});
export type SweepCancelRequest = z.infer<typeof SweepCancelRequestSchema>;

/** POST /api/economy-chain/v1/sweeps/:id/retry — resume a failed sweep from where it stopped. */
export const SweepRetryRequestSchema = z.object({});
export type SweepRetryRequest = z.infer<typeof SweepRetryRequestSchema>;

/** Cancel/retry acknowledge with the sweep's new status. */
export const SweepActionResponseSchema = z.object({
  sweep: SweepSchema,
});
export type SweepActionResponse = z.infer<typeof SweepActionResponseSchema>;
